import React, { ChangeEvent, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { T, O, M, A } from 'TOMA';
import { InputText, InputFile, InputMedias } from 'form';
import { eRoles } from 'interfaces/Roles';
import s from './Event.module.scss';
import {
  Select,
  MenuItem,
  Typography,
  InputLabel,
  FormControl,
  Button,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import useRepo from '../../hooks/useRepo';
import useModalContext from '../../hooks/useModalContext';
import { iCompany } from '../../services/Company/Company.repository';
import { useTranslation } from 'react-i18next';
import routes from '../../router/routes';
import { CustomLink } from 'components/CustomLink';
import CheckIcon from '@mui/icons-material/Check';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';

export interface iForm {
  name: string;
  color: string;
  address: string;
  phone: string;
  title: string;
  footer: string;
  slug: string;
  contact: string;
  event_id: number | string;
  logo: string;
  right_logo: string;
  main_logo: string;
  client_id: string;
  client_secret: string;
}

const SIDEBAR_WIDTH = 230;
// Height of the app's sticky top header (#app-header Toolbar minHeight), so the
// fixed sidebar starts below it instead of being hidden underneath.
const HEADER_HEIGHT = 70;

interface SidebarItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  matchFn: (p: string) => boolean;
}

function Company(): JSX.Element {
  const { setModalContent } = useModalContext();
  const {
    companyRepository,
    userRepository,
    mediaRepository,
    codificationRepository,
  } = useApiContext();
  const { pathname } = useLocation();
  const user = userRepository.getUser();
  const [loadingForm, setLoadingForm] = useState(false);
  const [successForm, setSuccessForm] = useState(false);
  const [errorForm, setErrorForm] = useState<string>();
  const isAdmin =
    user?.role === eRoles.ADMIN || user?.role === eRoles.MODERATOR;
  const form = useForm<iForm>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
  });
  const { handleSubmit, watch, control, reset, setValue, getValues, register } =
    form;
  const { response, loading } = useRepo(
    async () => await userRepository.getCompany().then((e) => e),
    [pathname]
  );
  const [file, setFile] = useState('');
  const [fileRight, setFileRight] = useState('');
  const [fileMain, setFileMain] = useState('');
  useEffect(() => {
    if (!loading && response) {
      setEvent(response.company.event_id);
      setEvents(response.events);
      setFile(response.logo);
      setFileRight(response.right_logo);
      setFileMain(response.main_logo);
      reset(response.company);
    }
  }, [loading, response, form.reset]);

  const pathSegments = window.location.pathname.split('/');
  const dynamicSegment = pathSegments[1];

  function onSubmit(data: iForm): void {
    setValue('slug', dynamicSegment || 'businessfrance');
    setValue('logo', file);
    setValue('right_logo', fileRight);
    setValue('main_logo', fileMain);
    userRepository
      .updateCompany({
        ...data,
        logo: file,
        right_logo: fileRight,
        main_logo: fileMain,
      })
      .then(() => {
        setSuccessForm(true);
        setErrorForm('');
      })
      .catch((err: Error) => {
        setErrorForm(err.message);
      });
  }
  const { t } = useTranslation();
  const [reload, setReload] = useState<number>(0);
  const [event, setEvent] = useState<number | string>();
  const [events, setEvents] = useState<[]>([]);

  const sidebarItems = useMemo<SidebarItem[]>(
    () => [
      {
        label: t('header:users'),
        icon: <PeopleIcon fontSize="small" />,
        path: routes.profile.path,
        matchFn: (p) => p.includes('users'),
      },
      {
        label: t('header:events'),
        icon: <CalendarMonthIcon fontSize="small" />,
        path: routes.admEventList.path,
        matchFn: (p) => p.includes('events'),
      },
      {
        label: t('header:exhibitor'),
        icon: <GroupsIcon fontSize="small" />,
        path: routes.home.path,
        matchFn: (p) =>
          p.includes('exhibitor') && !p.includes('exhibitor-list'),
      },
      {
        label: t('header:company_settings'),
        icon: <BusinessIcon fontSize="small" />,
        path: routes.company.path,
        matchFn: (p) => p.includes('company'),
      },
    ],
    [t]
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F9FAFB' }}>
      {/* ── Left sidebar ── */}
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: '#fff',
          borderRight: '1px solid #eee',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: HEADER_HEIGHT,
          left: 0,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          overflowY: 'hidden',
        }}
      >
        {/* Sidebar header */}
        <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '8px',
                bgcolor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BusinessIcon sx={{ fontSize: 18, color: '#555' }} />
            </Box>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#111' }}
            >
              {t('header:company_settings')}
            </Typography>
          </Box>
        </Box>

        {/* Nav items */}
        <List disablePadding sx={{ py: 1.5, flex: 1 }}>
          {sidebarItems.map((item) => {
            const isActive = item.matchFn(pathname);
            return (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  component={CustomLink as any}
                  to={item.path}
                  color="black"
                  textDecoration="none"
                  sx={{
                    px: 2,
                    py: 0.875,
                    mx: 1,
                    mb: 0.25,
                    borderRadius: '8px',
                    backgroundColor: isActive ? '#f5f5f5' : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive ? '#eeeeee' : '#f9f9f9',
                    },
                    transition: 'background-color 0.15s',
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: 30, color: isActive ? '#111' : '#bbb' }}
                  >
                    {isActive ? <CheckIcon sx={{ fontSize: 17 }} /> : item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '13px',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#111' : '#666',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* ── Main content (form — unchanged) ── */}
      <Box sx={{ flex: 1, minWidth: 0, marginLeft: `${SIDEBAR_WIDTH}px` }}>
        <T.Block mt="25">
          <M.Loading loading={false} error={null} fullWiewport>
            <T.Block
              w={pathname.includes('new-event') ? '100' : '75'}
              mx={pathname.includes('new-event') ? '0' : 'auto'}
              bg="white"
              borderRadius="20"
              mb="30"
            >
              <form
                onSubmit={handleSubmit(onSubmit)}
                encType="multipart/form-data"
                noValidate
              >
                <T.Block>
                  <T.Block p="20">
                    <A.Title
                      as="h1"
                      fontSize="48"
                      mb="30"
                      fw="black"
                      textAlign="center"
                    >
                      {t('company:company_informations')}
                    </A.Title>
                    <A.Line mb="20" />
                    <T.Block>
                      <InputText
                        label={t('company:name')}
                        name="name"
                        type="text"
                        control={control}
                        disabled={false}
                      />
                    </T.Block>
                    <T.Block>
                      <Controller
                        name="event_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            sx={{ marginBottom: '20px' }}
                          >
                            <InputLabel>{t('company:main_event')}</InputLabel>
                            <Select
                              {...field}
                              label={t('company:main_event')}
                              value={field.value || ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value !== '' ? e.target.value : null
                                )
                              }
                            >
                              {events?.map(({ value, label, year }) => (
                                <MenuItem key={value} value={value}>
                                  {label} {year ?? ''}
                                </MenuItem>
                              ))}
                              <MenuItem key={'select-event'} value={''}>
                                Select Empty
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </T.Block>
                    <T.Block>
                      <InputMedias
                        label={t('company:logo')}
                        information={t('company:logo')}
                        name="logo"
                        defaultValue={file}
                        control={control}
                        onInit={async (mediaId) => {
                          return await mediaRepository
                            .get(mediaId)
                            .then(({ id, w640, filename }) => ({
                              id,
                              src: w640,
                              filename,
                            }));
                        }}
                        onChange={async (file) => {
                          const formdata = new FormData();
                          formdata.append('media', file);
                          const result = await mediaRepository.upload(formdata);
                          if (result) {
                            setFile(result.id);
                            return result;
                          }
                          return null;
                        }}
                        onRemove={async (mediaId) => {
                          return await mediaRepository
                            .remove('logo', mediaId)
                            .then((e) => {
                              setFile('');
                            });
                        }}
                        onZoom={(src) => {
                          setModalContent(
                            <img src={src} style={{ maxWidth: '100%' }} />
                          );
                        }}
                        required
                      />
                    </T.Block>
                    <T.Block>
                      <InputMedias
                        label={t('company:right_header_logo')}
                        information={t('company:right_header_logo')}
                        name="right_logo"
                        defaultValue={fileRight}
                        control={control}
                        onInit={async (mediaId) => {
                          return await mediaRepository
                            .get(mediaId)
                            .then(({ id, w640, filename }) => ({
                              id,
                              src: w640,
                              filename,
                            }));
                        }}
                        onChange={async (file) => {
                          const formdata = new FormData();
                          formdata.append('media', file);
                          const result = await mediaRepository.upload(formdata);
                          if (result) {
                            setFileRight(result.id);
                            return result;
                          }
                          return null;
                        }}
                        onRemove={async (mediaId) => {
                          return await mediaRepository
                            .remove('right_logo', mediaId)
                            .then((e) => {
                              setFileRight('');
                              setValue('right_logo', '');
                            });
                        }}
                        onZoom={(src) => {
                          setModalContent(
                            <img src={src} style={{ maxWidth: '100%' }} />
                          );
                        }}
                        required
                      />
                    </T.Block>
                    <T.Block mt="5">
                      <InputText
                        label={t('company:title_of_the_plateform')}
                        name="title"
                        type="text"
                        control={control}
                        disabled={false}
                      />
                    </T.Block>
                    <T.Block>
                      <InputText
                        label={t('common:footer_text')}
                        name="footer"
                        type="text"
                        control={control}
                        disabled={false}
                      />
                    </T.Block>
                    <T.Block>
                      <InputText
                        label={t('company:address')}
                        name="address"
                        type="text"
                        control={control}
                        disabled={false}
                      />
                    </T.Block>
                    <T.Block>
                      <InputText
                        label={t('company:contact')}
                        name="contact"
                        type="text"
                        control={control}
                        disabled={false}
                      />
                    </T.Block>
                    <T.Block>
                      <InputText
                        label={t('company:phone')}
                        name="phone"
                        type="text"
                        control={control}
                        disabled={false}
                      />
                    </T.Block>
                    <T.Block mt="30">
                      <A.Title as="h2" fontSize="24" mb="10" fw="black">
                        {t('company:salesforce_credentials')}
                      </A.Title>
                      <A.Line mb="15" />
                    </T.Block>
                    <T.Block>
                      <InputText
                        label={t('company:client_id')}
                        name="client_id"
                        type="text"
                        control={control}
                        disabled={false}
                      />
                    </T.Block>
                    <T.Block>
                      <InputText
                        label={t('company:client_secret')}
                        name="client_secret"
                        type="text"
                        control={control}
                        disabled={false}
                      />
                    </T.Block>
                    <A.Alert alert="success" side="🎉" mb="15">
                      {successForm && 'Company updated successfully!'}
                    </A.Alert>
                    <A.Alert alert="error" side="⚠️" mb="15">
                      {errorForm && <A.InnerHTML html={errorForm} />}
                    </A.Alert>
                  </T.Block>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <A.Button
                      type="button"
                      bg="success"
                      loading={loadingForm}
                      onClick={async () => {
                        handleSubmit(() => onSubmit(getValues()))();
                      }}
                      className={s.submit_button}
                      disabled={false}
                    >
                      {t('company:save')}
                    </A.Button>
                  </div>
                </T.Block>
              </form>
            </T.Block>
          </M.Loading>
        </T.Block>
      </Box>
    </Box>
  );
}

export default Company;
